import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Upload, X, FileIcon, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { uploadCandidateFile, type FileType } from '@/lib/api';

interface FileUploaderProps {
  candidateId: string;
  onUploadComplete?: () => void;
  className?: string;
}

const FILE_TYPE_LABELS: Record<FileType, string> = {
  cv: 'CV / Resume',
  id_document: 'ID Document',
  certificate: 'Certificate',
  photo: 'Photo',
  other: 'Other',
};

const ACCEPTED_TYPES: Record<FileType, string> = {
  cv: '.pdf,.doc,.docx',
  id_document: '.pdf,.jpg,.jpeg,.png',
  certificate: '.pdf,.jpg,.jpeg,.png',
  photo: '.jpg,.jpeg,.png,.webp',
  other: '*',
};

interface PendingFile {
  file: File;
  fileType: FileType;
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

export function FileUploader({ candidateId, onUploadComplete, className }: FileUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedType, setSelectedType] = useState<FileType>('cv');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const newPendingFiles: PendingFile[] = Array.from(files).map((file) => ({
        file,
        fileType: selectedType,
        progress: 0,
        status: 'pending' as const,
      }));

      setPendingFiles((prev) => [...prev, ...newPendingFiles]);
    },
    [selectedType],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      addFiles(e.target.files);
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addFiles],
  );

  const removeFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateFileType = useCallback((index: number, fileType: FileType) => {
    setPendingFiles((prev) =>
      prev.map((pf, i) => (i === index ? { ...pf, fileType } : pf)),
    );
  }, []);

  const uploadFile = useCallback(
    async (index: number) => {
      const pendingFile = pendingFiles[index];
      if (!pendingFile || pendingFile.status !== 'pending') return;

      setPendingFiles((prev) =>
        prev.map((pf, i) =>
          i === index ? { ...pf, status: 'uploading' as const, progress: 0 } : pf,
        ),
      );

      try {
        await uploadCandidateFile(
          candidateId,
          pendingFile.file,
          pendingFile.fileType,
          (progress) => {
            setPendingFiles((prev) =>
              prev.map((pf, i) => (i === index ? { ...pf, progress } : pf)),
            );
          },
        );

        setPendingFiles((prev) =>
          prev.map((pf, i) =>
            i === index ? { ...pf, status: 'complete' as const, progress: 100 } : pf,
          ),
        );

        // Invalidate files cache
        queryClient.invalidateQueries({ queryKey: ['candidateFiles', candidateId] });
        onUploadComplete?.();

        // Remove completed file after a delay
        setTimeout(() => {
          setPendingFiles((prev) => prev.filter((_, i) => i !== index));
        }, 2000);
      } catch (error) {
        setPendingFiles((prev) =>
          prev.map((pf, i) =>
            i === index
              ? {
                  ...pf,
                  status: 'error' as const,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : pf,
          ),
        );
      }
    },
    [pendingFiles, candidateId, queryClient, onUploadComplete],
  );

  const uploadAllPending = useCallback(async () => {
    const pendingIndexes = pendingFiles
      .map((pf, i) => (pf.status === 'pending' ? i : -1))
      .filter((i) => i !== -1);

    for (const index of pendingIndexes) {
      await uploadFile(index);
    }
  }, [pendingFiles, uploadFile]);

  const hasPendingFiles = pendingFiles.some((pf) => pf.status === 'pending');
  const hasUploadingFiles = pendingFiles.some((pf) => pf.status === 'uploading');

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* File Type Selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-text-secondary">Document Type:</label>
        <Select value={selectedType} onValueChange={(v) => setSelectedType(v as FileType)}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(FILE_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative rounded-lg border-2 border-dashed p-8 transition-colors',
          isDragging
            ? 'border-primary-500 bg-primary-50'
            : 'border-white/20 bg-bg-secondary hover:border-gray-400',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES[selectedType]}
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
        />

        <div className="flex flex-col items-center justify-center text-center">
          <Upload
            className={cn(
              'h-10 w-10 mb-3',
              isDragging ? 'text-primary-500' : 'text-text-tertiary',
            )}
          />
          <p className="text-sm text-text-secondary">
            <label
              htmlFor="file-upload"
              className="font-medium text-emerald-500 hover:text-emerald-400 cursor-pointer"
            >
              Click to upload
            </label>{' '}
            or drag and drop
          </p>
          <p className="text-xs text-text-tertiary mt-1">
            {selectedType === 'other'
              ? 'Any file type'
              : `Accepted: ${ACCEPTED_TYPES[selectedType]}`}
          </p>
        </div>
      </div>

      {/* Pending Files List */}
      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-text-secondary">Files to upload</h4>
            {hasPendingFiles && (
              <Button
                size="sm"
                onClick={uploadAllPending}
                disabled={hasUploadingFiles}
              >
                {hasUploadingFiles ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload All'
                )}
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {pendingFiles.map((pf, index) => (
              <div
                key={`${pf.file.name}-${index}`}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3',
                  pf.status === 'complete'
                    ? 'border-green-200 bg-green-50'
                    : pf.status === 'error'
                      ? 'border-red-200 bg-red-50'
                      : 'border-white/10 bg-bg-tertiary',
                )}
              >
                <FileIcon className="h-8 w-8 text-text-tertiary flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {pf.file.name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-text-tertiary">
                    <span>{formatFileSize(pf.file.size)}</span>
                    {pf.status === 'pending' && (
                      <Select
                        value={pf.fileType}
                        onValueChange={(v) => updateFileType(index, v as FileType)}
                      >
                        <SelectTrigger className="h-6 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(FILE_TYPE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {pf.status !== 'pending' && (
                      <span className="text-text-tertiary">
                        {FILE_TYPE_LABELS[pf.fileType]}
                      </span>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {pf.status === 'uploading' && (
                    <div className="mt-2">
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 transition-all duration-300"
                          style={{ width: `${pf.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-text-tertiary mt-1">{pf.progress}%</p>
                    </div>
                  )}

                  {/* Error Message */}
                  {pf.status === 'error' && pf.error && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                      <AlertCircle className="h-3 w-3" />
                      {pf.error}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {pf.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => uploadFile(index)}
                      >
                        Upload
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeFile(index)}
                        className="h-8 w-8"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {pf.status === 'uploading' && (
                    <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />
                  )}
                  {pf.status === 'complete' && (
                    <span className="text-sm text-green-600 font-medium">Done</span>
                  )}
                  {pf.status === 'error' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPendingFiles((prev) =>
                          prev.map((f, i) =>
                            i === index ? { ...f, status: 'pending' as const, error: undefined } : f,
                          ),
                        );
                      }}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
